// The browser side of pending-typed-code.ts (#6984): localStorage, the clock, and
// info-tier reporting. See that file for the rules.
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { describeError } from '@cf-shared/error-describe';
import { LoggingService } from 'services/logging.service';
import {
  PendingTypedCodeProblem,
  PendingTypedCodeProblemReporter,
  PendingTypedCodeStorage,
  rememberTypedCode,
  takeTypedCode,
} from './pending-typed-code';

@Injectable({ providedIn: 'root' })
export class PendingTypedCodeService {
  constructor(private logging: LoggingService) {}

  // every storage failure is an expected browser limit, and the only cost is retyping the code
  private readonly reportProblem: PendingTypedCodeProblemReporter = (problem, err) => {
    this.logging.trackSilentInfo(problem, { error: describeError(err) });
  };

  /** The browser's local storage, or undefined (reported) when the browser refuses access to it. */
  private storage(): PendingTypedCodeStorage | undefined {
    try {
      return window.localStorage;
    } catch (err) {
      this.reportProblem(PendingTypedCodeProblem.Unreadable, err);
      return undefined;
    }
  }

  /** Keep a code typed while signed out. */
  remember(code: string): void {
    const storage = this.storage();
    if (storage) rememberTypedCode(storage, code, DateTime.utc().toISO(), this.reportProblem);
  }

  /** Take the kept code, if one is still fresh. Taken once: it is removed as it is read. */
  take(): string | undefined {
    const storage = this.storage();
    return storage ? takeTypedCode(storage, DateTime.utc().toISO(), this.reportProblem) : undefined;
  }
}
